import { request } from "@/api/client";
import type {
  LedgerAccount,
  LedgerAccountInput,
  LedgerAccountUpdate,
  LedgerEntry,
  LedgerEntryInput,
  LedgerJournal,
  LedgerWorksheetEntry,
  LedgerLineUpdatePayload,
  LedgerCategorySuggestion,
} from "@/types/ledger";

export type LedgerEntryFilters = {
  status?: string;
  limit?: number;
};

export function fetchLedgerAccounts(): Promise<LedgerAccount[]> {
  return request("/ledger/accounts");
}

export function createLedgerAccount(payload: LedgerAccountInput): Promise<LedgerAccount> {
  return request("/ledger/accounts", { method: "POST", body: payload });
}

export function updateLedgerAccount(accountId: number, payload: LedgerAccountUpdate): Promise<LedgerAccount> {
  return request(`/ledger/accounts/${accountId}`, { method: "PATCH", body: payload });
}

export function fetchAccountWorksheet(accountId: number, limit = 200): Promise<LedgerWorksheetEntry[]> {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  return request(`/ledger/accounts/${accountId}/worksheet?${params.toString()}`);
}

export function fetchLedgerJournals(): Promise<LedgerJournal[]> {
  return request("/ledger/journals");
}

export function fetchLedgerEntries(status?: string, limit = 100): Promise<LedgerEntry[]> {
  return listLedgerEntries({ status, limit });
}

export function listLedgerEntries(filters: LedgerEntryFilters = {}): Promise<LedgerEntry[]> {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (typeof filters.limit === "number") {
    params.set("limit", String(filters.limit));
  } else {
    params.set("limit", "100");
  }
  const suffix = params.toString();
  return request(`/ledger/entries${suffix ? `?${suffix}` : ""}`);
}

export function fetchLedgerEntry(entryId: string): Promise<LedgerEntry> {
  return request(`/ledger/entries/${encodeURIComponent(entryId)}`);
}

export function createLedgerEntry(payload: LedgerEntryInput): Promise<LedgerEntry> {
  return request("/ledger/entries", { method: "POST", body: payload });
}

export function postLedgerEntry(entryId: string): Promise<LedgerEntry> {
  return request(`/ledger/entries/${encodeURIComponent(entryId)}/post`, { method: "POST" });
}

export function updateLedgerLine(entryId: string, lineId: number, payload: LedgerLineUpdatePayload) {
  return request(`/ledger/entries/${encodeURIComponent(entryId)}/lines/${lineId}`, {
    method: "PATCH",
    body: payload,
  });
}

export function suggestLedgerCategory(entryId: string, lineId: number): Promise<LedgerCategorySuggestion> {
  return request(`/ledger/entries/${encodeURIComponent(entryId)}/lines/${lineId}/suggest-category`, {
    method: "POST",
  });
}
